import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FiClipboard } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { createInitialsAvatar } from "../../utils/initialsAvatar";
import EmployeeHeader from "./employeeHeader";
import { EmployeeTable } from "../../components/Tables";
import axios from "../../api/httpClient";
import { buildAssetUrl, getApiBaseUrl } from "../../data/authStorage";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";
import useCurrentEmployee from "../../hooks/useCurrentEmployee";
import ExpandableSearch from "../../components/ExpandableSearch";
import PaginationControls from "../../components/PaginationControls";
import ProfileChangeRequestsDialog from "../../components/employee/ProfileChangeRequestsDialog";
import { GlobalState } from "../../data/Context";

const PAGE_SIZE = 10;

const formatEmployeeName = (value) => `${value || "Unnamed employee"}`
  .trim()
  .toLowerCase()
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getEmployeesCacheKey = (organizationId, status, inviteFilter, scope, search = "") =>
  `cehr_employees_page_${organizationId || "unknown-org"}_${scope || "admin"}_${status}_${inviteFilter || "all"}_${search || "all"}`;

const getRoleText = (employee) =>
  [
    employee?.positionName,
    employee?.position?.name,
    employee?.roleName,
    employee?.role?.name,
    employee?.assignmentLabel,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export default function StudentsTable() {
  const { organization } = useContext(GlobalState);
  const organizationId = organization?.id || organization?.organizationId || "unknown-org";
  const [dataOrganizationId, setDataOrganizationId] = useState(organizationId);
  const [selectedStatus, setSelectedStatus] = useState("approved");
  const [inviteResponseFilter, setInviteResponseFilter] = useState("pending");
  const [employeeData, setEmployeeData] = useState([]);
  const [showProfileChangeRequests, setShowProfileChangeRequests] = useState(false);
  const [pendingProfileChangeCount, setPendingProfileChangeCount] = useState(0);
  const [invitedStaffData, setInvitedStaffData] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [employeesError, setEmployeesError] = useState("");
  const [inviteActionMessage, setInviteActionMessage] = useState("");
  const [reInvitingId, setReInvitingId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const requestSequenceRef = useRef(0);
  const apiBaseUrl = getApiBaseUrl();
  const {
    companyAccount: organizationAccount,
    currentEmployee,
    loading: currentEmployeeLoading,
  } = useCurrentEmployee();
  const companyAccount = organizationAccount || currentEmployee?.modulePermissions?.includes("employees");
  const roleText = getRoleText(currentEmployee);
  const isHod = Boolean(
    !companyAccount &&
      currentEmployee &&
      (currentEmployee.isHeadOfDepartment ||
        roleText.includes("hod") ||
        roleText.includes("head of department")),
  );
  const canManageEmployees = companyAccount;
  const canViewEmployees = canManageEmployees || isHod;
  const hodDepartmentId =
    currentEmployee?.department?.id ||
    currentEmployee?.departmentId ||
    currentEmployee?.role?.departmentId ||
    "";
  const listScope = isHod ? `hod_${hodDepartmentId || "department"}` : "admin";

  const navigate = useNavigate();

  useEffect(() => {
    if (!apiBaseUrl || !companyAccount) return undefined;
    let active = true;
    const loadPendingCount = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/organizations/profile-change-requests?status=Pending`);
        if (active) setPendingProfileChangeCount(response?.data?.pendingCount || 0);
      } catch {
        if (active) setPendingProfileChangeCount(0);
      }
    };
    loadPendingCount();
    window.addEventListener("focus", loadPendingCount);
    return () => { active = false; window.removeEventListener("focus", loadPendingCount); };
  }, [apiBaseUrl, companyAccount]);

  const loadEmployees = useCallback(
    async ({ force = false } = {}) => {
      const requestId = ++requestSequenceRef.current;

      if (!apiBaseUrl) {
        setEmployeesError("API base URL is not configured.");
        return;
      }

      if (currentEmployeeLoading) {
        return;
      }

      if (!canViewEmployees) {
        setEmployeeData([]);
        setInvitedStaffData([]);
        setEmployeesLoading(false);
        setEmployeesError("Only organization admins and department heads can view employees.");
        return;
      }

      if (isHod && !hodDepartmentId) {
        setEmployeeData([]);
        setInvitedStaffData([]);
        setEmployeesLoading(false);
        setEmployeesError("Your department profile is not complete, so the department staff list cannot be loaded.");
        return;
      }

      const cacheKey = getEmployeesCacheKey(
        organizationId,
        selectedStatus,
        inviteResponseFilter,
        listScope,
        debouncedSearch,
      );
      const cachedData = readSessionCache(cacheKey, null);

      if (cachedData) {
        setDataOrganizationId(organizationId);
        setEmployeeData(cachedData.employeeData || []);
        setInvitedStaffData(cachedData.invitedStaffData || []);
        setEmployeesLoading(false);
        if (!force) {
          return;
        }
      }

      if (!cachedData) {
        setEmployeesLoading(true);
      }
      setEmployeesError("");

      if (companyAccount && selectedStatus === "invited") {
        try {
          const response = await axios.get(
            `${apiBaseUrl}/organizations/employees/invite/list?status=${encodeURIComponent(inviteResponseFilter)}&search=${encodeURIComponent(debouncedSearch)}`,
          );

          if (requestId !== requestSequenceRef.current) {
            return;
          }

          const invites = response?.data?.invites || [];
          setDataOrganizationId(organizationId);

          setInvitedStaffData(
            invites.map((invite) => ({
              id: invite.id,
              email: invite.email || "",
              status: invite.status || "Pending",
              invitedAt: invite.invitedAt || "",
              expiresAt: invite.expiresAt || "",
              respondedAt: invite.respondedAt || "",
              emailDeliveryStatus: invite.emailDeliveryStatus || "Unknown",
              lastError: invite.lastError || "",
              respondedEmployeeName: invite?.respondedEmployee?.name || "-",
            })),
          );
          writeSessionCache(cacheKey, {
            employeeData: [],
            invitedStaffData: invites.map((invite) => ({
              id: invite.id,
              email: invite.email || "",
              status: invite.status || "Pending",
              invitedAt: invite.invitedAt || "",
              expiresAt: invite.expiresAt || "",
              respondedAt: invite.respondedAt || "",
              emailDeliveryStatus: invite.emailDeliveryStatus || "Unknown",
              lastError: invite.lastError || "",
              respondedEmployeeName: invite?.respondedEmployee?.name || "-",
            })),
          });
        } catch (requestError) {
          if (requestId !== requestSequenceRef.current) {
            return;
          }

          const message =
            requestError?.response?.data?.message ||
            "Unable to load invited staff list.";
          setEmployeesError(
            Array.isArray(message) ? message.join(", ") : message,
          );
          setInvitedStaffData([]);
        } finally {
          if (requestId === requestSequenceRef.current) {
            setEmployeesLoading(false);
          }
        }

        return;
      }

      try {
        const endpoint = isHod
          ? `${apiBaseUrl}/organizations/departments/${encodeURIComponent(hodDepartmentId)}/staff?search=${encodeURIComponent(debouncedSearch)}`
          : `${apiBaseUrl}/organizations/employees?status=${encodeURIComponent(selectedStatus)}&search=${encodeURIComponent(debouncedSearch)}`;
        const response = await axios.get(endpoint);

        if (requestId !== requestSequenceRef.current) {
          return;
        }

        const records = (isHod ? response?.data?.staff || [] : response?.data?.employees || []).filter((employee) => !employee.isContractor);
        setDataOrganizationId(organizationId);

        const resolveEmployeeImage = (imagePath, name) =>
          buildAssetUrl(imagePath) || createInitialsAvatar(name);

        const mappedRecords = records.map((employee) => {
          const toolNames = Array.isArray(employee.toolAssignments)
            ? employee.toolAssignments
                .filter((assignment) => assignment?.status === "Assigned")
                .map((assignment) => assignment?.tool?.name)
                .filter(Boolean)
            : [];

          return {
            id: employee.id,
            image: resolveEmployeeImage(
              employee.profilePhotoUrl || employee.image,
              employee.name || employee.fullName || `${employee.firstName || ""} ${employee.lastName || ""}`.trim(),
            ),
            name: formatEmployeeName(
              employee.name || employee.fullName || `${employee.firstName || ""} ${employee.lastName || ""}`,
            ),
            idNo: employee.idNo || employee.employeeNumber || "",
            phoneNumber: employee.phoneNumber || employee.phone || "",
            emailaddress:
              employee.emailAddress ||
              employee.officialEmail ||
              employee.personalEmail ||
              "",
            department: Array.from(new Set([
              ...(Array.isArray(employee.departments)
                ? employee.departments.map((department) => department?.name)
                : []),
              employee.department?.name,
              employee.departmentName,
              typeof employee.department === "string" ? employee.department : "",
              currentEmployee?.department?.name,
            ].filter(Boolean))).join(", "),
            dateJoined: employee.dateJoined || "",
            noticePeriod:
              employee.noticePeriod || employee.offboardingNoticePeriod || "",
            toolsAssigned:
              toolNames.length > 0
                ? toolNames.join(", ")
                : employee.toolsAssigned || employee.offboardingToolsAssigned || "",
            status:
              employee.status ||
              (selectedStatus === "offboarded"
                ? "Offboarded"
                : selectedStatus === "pending"
                  ? "Pending"
                  : "Approved"),
          };
        });

        setEmployeeData(mappedRecords);
        writeSessionCache(cacheKey, {
          employeeData: mappedRecords,
          invitedStaffData: [],
        });
      } catch (requestError) {
        if (requestId !== requestSequenceRef.current) {
          return;
        }

        const message =
          requestError?.response?.data?.message || "Unable to load employees.";
        setEmployeesError(
          Array.isArray(message) ? message.join(", ") : message,
        );
        setEmployeeData([]);
      } finally {
        if (requestId === requestSequenceRef.current) {
          setEmployeesLoading(false);
        }
      }
    },
    [
      apiBaseUrl,
      canViewEmployees,
      companyAccount,
      currentEmployee?.department?.name,
      currentEmployeeLoading,
      debouncedSearch,
      hodDepartmentId,
      inviteResponseFilter,
      isHod,
      listScope,
      organizationId,
      selectedStatus,
    ],
  );

  useEffect(() => {
    setDataOrganizationId(organizationId);
    setEmployeeData([]);
    setInvitedStaffData([]);
    setEmployeesLoading(true);
    setEmployeesError("");
  }, [organizationId]);

  useEffect(() => {
    loadEmployees({ silent: true });
  }, [loadEmployees]);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedSearch(searchTerm.trim()),
      300,
    );
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        loadEmployees({ silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [loadEmployees]);

  const viewEmployee = (employee) => {
    if (!employee?.id) {
      return;
    }

    navigate(`/employee/employeespage?id=${encodeURIComponent(employee.id)}`);
  };

  const viewOffboardingDetails = (employee) => {
    if (!employee?.id) {
      return;
    }

    navigate(`/employee/employeespage?id=${encodeURIComponent(employee.id)}`);
  };

  const handleStatusChange = (status, nextInviteFilter) => {
    setCurrentPage(1);
    setSelectedStatus(status);

    if (status === "invited" && nextInviteFilter) {
      setInviteResponseFilter(nextInviteFilter);
    }
  };

  const formatDate = (value) => {
    if (!value) {
      return "-";
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }

    return parsed.toLocaleDateString();
  };

  const handleReInvite = async (invite) => {
    if (!invite?.id || !invite?.email || invite.status !== "Pending") {
      return;
    }

    try {
      setReInvitingId(invite.id);
      setInviteActionMessage("");
      const response = await axios.post(`${apiBaseUrl}/organizations/employees/invite`, {
        emails: invite.email,
      });
      if (!response?.data?.summary?.sentCount) {
        const reason =
          response?.data?.failedDetails?.[0]?.reason ||
          "The invitation email could not be delivered.";
        setInviteActionMessage(`Re-invite failed: ${reason}`);
        await loadEmployees({ force: true });
        return;
      }
      setInviteActionMessage(`A new invitation was sent to ${invite.email}.`);
      await loadEmployees({ force: true });
    } catch (requestError) {
      const detail =
        requestError?.response?.data?.message ||
        requestError?.response?.data?.failedDetails?.[0]?.reason ||
        "Unable to resend this invitation.";
      setInviteActionMessage(
        `Re-invite failed: ${Array.isArray(detail) ? detail.join(", ") : detail}`,
      );
    } finally {
      setReInvitingId("");
    }
  };

  const loadingMessage =
    selectedStatus === "invited"
      ? `Loading ${inviteResponseFilter} invited staff...`
      : `Loading ${selectedStatus} staff...`;

  const matchesSearch = useCallback((record) => {
    const query = debouncedSearch.toLowerCase();
    if (!query) return true;
    return Object.values(record).some((value) =>
      String(value ?? "").toLowerCase().includes(query),
    );
  }, [debouncedSearch]);

  const visibleEmployees = useMemo(
    () => (dataOrganizationId === organizationId ? employeeData : []).filter(matchesSearch),
    [dataOrganizationId, employeeData, matchesSearch, organizationId],
  );
  const visibleInvites = useMemo(
    () => (dataOrganizationId === organizationId ? invitedStaffData : []).filter(matchesSearch),
    [dataOrganizationId, invitedStaffData, matchesSearch, organizationId],
  );
  const activeRecords = selectedStatus === "invited" ? visibleInvites : visibleEmployees;
  const totalPages = Math.max(1, Math.ceil(activeRecords.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedEmployees = useMemo(
    () => visibleEmployees.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [safePage, visibleEmployees],
  );
  const paginatedInvites = useMemo(
    () => visibleInvites.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [safePage, visibleInvites],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, inviteResponseFilter]);

  return (
    <div className="dashboard-enter w-full min-w-0 max-w-full overflow-x-hidden p-4 font-Nunito sm:p-6">
      <div className="mb-4 flex min-w-0 max-w-full flex-wrap items-center justify-between gap-3">
        <EmployeeHeader
          selectedStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          inviteResponseFilter={inviteResponseFilter}
          onInviteResponseFilterChange={setInviteResponseFilter}
          readOnly={!canManageEmployees}
          title={isHod ? "Department Employees" : "Employees"}
          subtitle={isHod ? "Staff assigned to your department." : ""}
        />
        {companyAccount ? <button type="button" onClick={() => setShowProfileChangeRequests(true)} className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-[#1234CD] bg-white px-3 py-2 text-xs font-semibold text-[#1234CD] hover:bg-blue-50"><FiClipboard />Profile update requests{pendingProfileChangeCount > 0 ? <span className="rounded-full bg-red-600 px-2 py-0.5 text-[10px] text-white">{pendingProfileChangeCount}</span> : null}</button> : null}
      </div>

      {showProfileChangeRequests ? <ProfileChangeRequestsDialog onClose={() => setShowProfileChangeRequests(false)} onPendingCountChange={setPendingProfileChangeCount} /> : null}

      {canViewEmployees ? (
        <ExpandableSearch
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search employees"
          label="Search employees"
          className="mb-4"
        />
      ) : null}

      {currentEmployeeLoading || employeesLoading ? (
        <FetchLoadingState
          message={currentEmployeeLoading ? "Checking employee access..." : loadingMessage}
          className="min-h-[220px]"
        />
      ) : !canViewEmployees ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-5 text-sm text-amber-800">
          Only organization admins and department heads can view the employee directory.
        </div>
      ) : companyAccount && selectedStatus === "invited" ? (
        <div className="w-full min-w-0 max-w-full">
          {inviteActionMessage ? (
            <div role="status" className={`mb-3 rounded-lg border px-4 py-3 text-sm ${inviteActionMessage.startsWith("Re-invite failed") ? "border-red-200 bg-red-50 text-red-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
              {inviteActionMessage}
            </div>
          ) : null}
          <div className="overflow-x-auto overscroll-x-contain">
          <table className="min-w-[720px] w-full text-left">
            <thead>
              <tr className="text-[#00000066] text-base">
                <th className="py-2 px-4">Invite Email</th>
                <th className="py-2 px-4">Status</th>
                <th className="py-2 px-4">Invited At</th>
                <th className="py-2 px-4">Expires At</th>
                <th className="py-2 px-4">Responded At</th>
                <th className="py-2 px-4">Delivery</th>
                <th className="py-2 px-4">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white rounded-lg">
              {paginatedInvites.map((item) => (
                <tr
                  key={item.id}
                  className="shadow-sm text-sm sm:text-base"
                >
                  <td className="py-4 px-4">{item.email || "-"}</td>
                  <td className="py-4 px-4">
                    <span
                      className={`font-medium ${
                        item.status === "Responded"
                          ? "text-green-600"
                          : "text-amber-600"
                      }`}
                    >
                      {item.status}
                    </span>
                  </td>
                  <td className="py-4 px-4">{formatDate(item.invitedAt)}</td>
                  <td className="py-4 px-4">{formatDate(item.expiresAt)}</td>
                  <td className="py-4 px-4">{formatDate(item.respondedAt)}</td>
                  <td className="py-4 px-4">
                    <span
                      className={`font-medium ${
                        item.emailDeliveryStatus === "Sent"
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {item.emailDeliveryStatus}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    {item.status === "Pending" ? (
                      <button
                        type="button"
                        onClick={() => handleReInvite(item)}
                        disabled={reInvitingId === item.id}
                        className="rounded-lg border border-[#1234CD] px-3 py-2 text-sm font-semibold text-[#1234CD] transition hover:bg-[#F4F7FF] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {reInvitingId === item.id ? "Sending..." : "Re-invite"}
                      </button>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <PaginationControls page={safePage} pageSize={PAGE_SIZE} totalItems={visibleInvites.length} onPageChange={setCurrentPage} />
        </div>
      ) : (
        <EmployeeTable
          mode={selectedStatus === "offboarded" ? "offboarding" : "employees"}
          data={paginatedEmployees}
          onView={
            selectedStatus === "offboarded"
              ? viewOffboardingDetails
              : viewEmployee
          }
        />
      )}

      {selectedStatus !== "invited" && !employeesLoading ? (
        <PaginationControls page={safePage} pageSize={PAGE_SIZE} totalItems={visibleEmployees.length} onPageChange={setCurrentPage} />
      ) : null}

      {selectedStatus === "invited" &&
      !employeesLoading &&
      visibleInvites.length === 0 &&
      !employeesError ? (
        <p className="mt-4 text-sm text-gray-500">
          No invited staff found for the selected filter.
        </p>
      ) : null}

      {employeesError ? (
        <p
          className="mt-4 text-sm text-red-600"
          role="alert"
        >
          {employeesError}
        </p>
      ) : null}
    </div>
  );
}
